import { useMemo, useState } from "react";
import { Spinner } from "@repo/ui/components/Base/Spinner";
import { DataTable } from "@repo/ui/components/Base/DataTable";
import { PublishConfirmation } from "../dialog/PublishConfirmation";
import { EditMeetingSheet } from "./EditMeetingSheet";
import { layoutDefaults, useColumns } from "./MeetingsDataTableColumns";
import { useNavigate } from "react-router-dom";
import { AppUrlPath } from "@/lib/AppUrlPath";
import { DocumentPreviewer } from "../dialog/DocumentPreviewer";
import { Loader } from "../common/Loader";
import { useUserColumns } from "@/hooks/useUserColumns";
import { KEYS } from "@/lib/constants";
import { ReoccurringMeetingDialog } from "./ReoccurringMeetingDialog";
import { addNestedMeetingTags } from "@/lib/tags";
import { ArchiveMeetingConfirmation } from "../dialog/ArchiveMeetingConfirmation";
import { downloadFile } from "@repo/shared/utils";
import { ErrorsDialog } from "./ErrorsDialog";
import { meetingService } from "@/services/meetingService";
import { useMeetingList } from "@/hooks/queryHooks/meetingHooks";
import { useActiveTeamIdOrOrg } from "@/hooks/useActiveTeam";

export const MeetingsTable = ({ tenantId }: { tenantId: string }) => {
  const navigate = useNavigate();
  const { data: meetings } = useMeetingList(
    tenantId,
    useActiveTeamIdOrOrg() ?? ""
  );
  const [loading, setLoading] = useState(false);

  const [activeId, setActiveId] = useState<string>();

  const activeMeeting = useMemo(
    () => meetings?.find((d) => d.id === activeId),
    [activeId, meetings]
  );

  const [publishErrors, setPublishErrors] = useState("");

  const [documentPreviewOpen, setDocumentPreviewOpen] = useState(false);

  const [editSheetOpen, setEditSheetOpen] = useState(false);
  const [archiveOpen, setArchiveOpen] = useState(false);

  const [publishErrorsOpen, setPublishErrorsOpen] = useState(false);
  const [reoccurringOpen, setReoccurringOpen] = useState(false);
  const [publishOpen, setPublishOpen] = useState(false);
  const [rePublishOpen, setRePublishOpen] = useState(false);

  const { defaultState, saveTableLayout, resetTableLayout } = useUserColumns(
    KEYS.MEETING_TABLE_COLUMNS,
    layoutDefaults
  );

  const handleQuickEdit = (id: string) => {
    setEditSheetOpen(true);
    setActiveId(id);
  };

  const handleEdit = (id: string) => {
    navigate(new AppUrlPath(tenantId).getMeetingPath(`${id}`) + "#edit=true");
  };

  const handleReoccurring = (id: string) => {
    setActiveId(id);
    setReoccurringOpen(true);
  };

  const handlePublishRequest = (id: string) => {
    setActiveId(id);
    setPublishOpen(true);
  };

  const handleRePublishRequest = (id: string) => {
    setActiveId(id);
    setRePublishOpen(true);
  };

  const handleArchive = (docId: string) => {
    setActiveId(docId);
    setArchiveOpen(true);
  };

  const handleCopyPdf = (docId: string) => {
    const activeMeeting = meetings?.find((meeting) => meeting.id === docId);
    if (!activeMeeting) return;
    if (!activeMeeting.agendaUrl) return;

    navigator.clipboard.writeText(activeMeeting.agendaUrl);
  };

  const handleDownloadPdf = (docId: string) => {
    const activeMeeting = meetings?.find((meeting) => meeting.id === docId);
    if (!activeMeeting) return;
    if (!activeMeeting.agendaUrl) return;
    downloadFile(activeMeeting.agendaUrl);
  };

  const handlePreview = (docId: string) => {
    setActiveId(docId);
    setDocumentPreviewOpen(true);
  };

  return (
    <>
      <Loader loading={loading || !meetings} />
      {!meetings && <Spinner />}

      {activeMeeting && (
        <DocumentPreviewer
          key={"preview" + activeId}
          title={activeMeeting.name}
          open={documentPreviewOpen}
          setOpen={setDocumentPreviewOpen}
          documentUri={
            activeMeeting.agendaPublishedAt && activeMeeting.agendaUrl
          }
        />
      )}

      {activeId && (
        <PublishConfirmation
          key={"publish" + activeId}
          open={publishOpen || rePublishOpen}
          setOpen={(arg) => {
            setPublishOpen(arg);
            setRePublishOpen(arg);
          }}
          itemId={activeId}
          republish={rePublishOpen}
          onConfirm={async (meetingId, { sendEmails }) => {
            setLoading(true);
            const errors = await meetingService.publishMeeting({
              tenantId,
              meetingId,
              sendEmails,
            });
            setLoading(false);

            if (errors) {
              setPublishErrors(
                "The document finished publishing, but the following warnings / errors occurred when publishing:\n\n" +
                  errors
              );
              setPublishErrorsOpen(true);
            }
          }}
        />
      )}

      {!!publishErrors && (
        <ErrorsDialog
          title="Errors While Publishing Meeting"
          errors={publishErrors}
          open={publishErrorsOpen}
          setOpen={(open: boolean) => {
            if (!open) setPublishErrors("");
            setPublishErrorsOpen(open);
          }}
        />
      )}

      {activeMeeting && activeId && (
        <ReoccurringMeetingDialog
          key={"reoccurring" + activeId}
          tenantId={tenantId}
          meeting={activeMeeting}
          open={reoccurringOpen}
          setOpen={setReoccurringOpen}
        />
      )}

      {activeMeeting && activeId && (
        <ArchiveMeetingConfirmation
          key={"archive" + activeId}
          open={archiveOpen}
          setOpen={setArchiveOpen}
          tenantId={tenantId}
          meeting={activeMeeting}
        />
      )}

      {activeId && (
        <EditMeetingSheet
          key={"edit" + activeId}
          open={editSheetOpen}
          setOpen={setEditSheetOpen}
          tenantId={tenantId}
          meetingId={activeId}
        />
      )}
      <div className="flex flex-col flex-grow min-h-[60vh]">
        <DataTable
          columns={useColumns({
            tenantId,
            handleQuickEdit,
            handleEdit,
            handleReoccurring,
            handlePublishRequest,
            handleRePublishRequest,
            handleArchive,
            handleCopyPdf,
            handleDownloadPdf,
            handlePreview,
          })}
          data={
            meetings?.map((meeting) =>
              addNestedMeetingTags(meeting, [
                // "meeting",
                // "agenda",
                // "agendaModule",
                "agendaItem",
                // "document",
              ])
            ) ?? []
          }
          includeMainFilter={false}
          userDefaults={defaultState}
          factoryDefaults={layoutDefaults}
          onLayoutSave={saveTableLayout}
          onLayoutReset={resetTableLayout}
        />
      </div>
    </>
  );
};
